import { useEffect, useRef, useState } from 'react'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { CircularProgress, useMediaQuery } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { verifyOtp, requestOtp } from '@/shared/api/otp'
import { getCustomer } from '@/shared/api/customer'
import { normalizeError } from '@/lib/apiErrors'
import { decodeJwtPayload } from '@/lib/jwtDecode'
import { logWarning } from '@/services/errorService'
import { useAppDispatch } from '@/store'
import { setCredentials } from '@/store/authSlice'
import { colors, breakpoints } from '@/styles/designTokens'
import { getVerifyOtpErrorMessageKey } from '../../lib/verifyOtpErrorCodeMapper'
import { getErrorMessageKey } from '../../lib/errorCodeMapper'
import { extractOtpDigits } from '../../lib/extractOtpDigits'
import {
	StyledContentBox,
	StyledVerifyTitle,
	StyledVerifyText,
	StyledOtpInputContainer,
	StyledOtpInput,
	StyledErrorBox,
	StyledErrorText,
	StyledButton,
	StyledFooterBox,
	StyledButtonContainer,
	StyledHelperLinksBox,
	StyledHelperText,
	StyledResultIcon,
	StyledResultTitle,
	StyledResultMessage,
} from '../../SignInModal.styled'
import { StyledVerifyContentWrapper, StyledLoadingContent } from './VerifyOtpStep.styled'
import type { StepComponentProps } from '../../SignInModal.types'

type VerifyState = 'input' | 'success' | 'failure'

const RESEND_COUNTDOWN_SECONDS = 30

// How long the success screen is shown before redirecting/closing the modal.
const SUCCESS_REDIRECT_DELAY_MS = 1500

export function VerifyOtpStep({ state, setState, locationId, onLoginSuccess }: StepComponentProps) {
	const dispatch = useAppDispatch()
	const { t } = useFormatMessage()
	const isMobile = useMediaQuery(`(max-width: ${breakpoints.mobile}px)`)
	const [verifyState, setVerifyState] = useState<VerifyState>('input')
	const [resendCountdown, setResendCountdown] = useState(RESEND_COUNTDOWN_SECONDS)
	const isVerifyingRef = useRef(false)
	const isResendingRef = useRef(false)
	const firstOtpInputRef = useRef<HTMLInputElement | null>(null)
	const otpDigits = state.otpInput.split('').concat(Array(6 - state.otpInput.length).fill(''))

	// Countdown timer for resend code (Figma: "Try again in 30 seconds")
	useEffect(() => {
		if (resendCountdown <= 0) return
		const timer = setInterval(() => {
			setResendCountdown((prev) => (prev <= 1 ? 0 : prev - 1))
		}, 1000)
		return () => clearInterval(timer)
	}, [resendCountdown])

	// Auto-close on success
	useEffect(() => {
		if (verifyState !== 'success') return
		const timer = setTimeout(() => {
			onLoginSuccess?.()
		}, SUCCESS_REDIRECT_DELAY_MS)
		return () => clearTimeout(timer)
	}, [verifyState, onLoginSuccess])

	// Focus on first OTP input when verify email/phone is visible and in input state
	useEffect(() => {
		if (verifyState === 'input' && firstOtpInputRef.current) {
			firstOtpInputRef.current.focus()
		}
	}, [verifyState])

	const handleOtpChange = (index: number, rawValue: string) => {
		// maxLength is 6 so browser autofill is not truncated, which means a keystroke into an
		// already-filled box now arrives as a 2-char value. Treat that as replacing the box,
		// not as a pasted code — otherwise typing over one digit would wipe the other five.
		const current = otpDigits[index] || ''
		const value =
			rawValue.length === 2 && current !== '' && rawValue.startsWith(current)
				? rawValue.slice(1)
				: rawValue

		let newOtp = ''

		if (value.length > 1) {
			// Autofill (autocomplete="one-time-code") delivers the whole code into one input,
			// on some platforms with surrounding text — extract the digits rather than reject.
			const digits = extractOtpDigits(value)
			if (!digits) return
			newOtp = digits
		} else {
			// Only allow digits
			if (!/^\d*$/.test(value)) return
			// Handle single digit: replace at current index
			newOtp = otpDigits.map((digit, i) => (i === index ? value : digit)).join('')
		}

		setState((prev) => ({
			...prev,
			otpInput: newOtp.slice(0, 6),
			errorMessage: null,
		}))

		// Move to next input if single digit entered
		if (value.length === 1 && value && index < 5) {
			const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement
			nextInput?.focus()
		}

		// Move focus to last filled digit if pasted multiple digits
		if (value.length > 1) {
			const lastFilledIndex = Math.min(newOtp.length - 1, 5)
			if (lastFilledIndex >= 0 && lastFilledIndex < 6) {
				const focusInput = document.getElementById(
					`otp-${lastFilledIndex}`,
				) as HTMLInputElement
				focusInput?.focus()
			}
		}

		// Auto-submit when all 6 digits are filled
		if (newOtp.length === 6) {
			void handleVerifyOtp(newOtp)
		}
	}

	const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Backspace' && !state.otpInput[index] && index > 0) {
			const prevInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement
			prevInput?.focus()
		}
	}

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault()
		// Copying from an SMS rarely yields bare digits — iOS copies the whole message body and
		// Android selection usually includes surrounding text or a newline. Extract the code
		// instead of requiring an exact-digit match, which previously failed silently.
		const digits = extractOtpDigits(e.clipboardData.getData('text'))
		if (!digits) return

		setState((prev) => ({
			...prev,
			otpInput: digits,
			errorMessage: null,
		}))

		// Focus on last filled digit
		const lastIndex = Math.min(digits.length - 1, 5)
		if (lastIndex >= 0) {
			setTimeout(() => {
				const focusInput = document.getElementById(`otp-${lastIndex}`) as HTMLInputElement
				focusInput?.focus()
			}, 0)
		}

		// Auto-submit if all 6 digits
		if (digits.length === 6) {
			setTimeout(() => {
				void handleVerifyOtp(digits)
			}, 0)
		}
	}

	const handleVerifyOtp = async (code: string) => {
		if (!code || code.length !== 6) {
			return
		}

		if (!state.correlationId || !state.channel || !state.identifier) {
			return
		}

		if (isVerifyingRef.current) {
			return
		}
		isVerifyingRef.current = true

		setState((prev) => ({
			...prev,
			isRequestInProgress: true,
			errorMessage: null,
		}))

		try {
			const response = await verifyOtp({
				channel: state.channel,
				identifier: state.identifier,
				otp: code,
				correlationId: state.correlationId,
			})

			// Decode and validate JWT payload
			const payload = decodeJwtPayload(response.accessToken)

			// Save token to Redux FIRST so axios interceptor can use it for getCustomer()
			// loginChannel from state.channel (set by SignInForm when requestOtp was called)
			// so ContactInformation authenticated render locks the correct field (phone vs email).
			const loginChannel = state.channel ?? 'SMS'
			dispatch(
				setCredentials({
					token: response.accessToken,
					user: {
						id: payload.user?.guid || payload.guid || '',
						email: payload.user?.email || '',
						phone: payload.user?.phone || '',
						name: payload.user?.name || '',
						loginChannel,
					},
				}),
			)

			// Try to fetch full customer profile with firstName, lastName, etc.
			try {
				const customerProfile = await getCustomer()

				// Merge customer profile with JWT data: use JWT values as fallbacks for missing profile fields
				dispatch(
					setCredentials({
						token: response.accessToken,
						user: {
							id: customerProfile.guid,
							email: customerProfile.email ?? payload.user?.email ?? '',
							phone: customerProfile.phone ?? payload.user?.phone ?? '',
							name: customerProfile.firstName
								? `${customerProfile.firstName} ${customerProfile.lastName || ''}`.trim()
								: payload.user?.name || '',
							firstName: customerProfile.firstName ?? undefined,
							lastName: customerProfile.lastName ?? undefined,
							guid: customerProfile.guid,
							loginChannel,
						},
					}),
				)
			} catch (customerError) {
				// TODO: handle 404 CUSTNF — if getCustomer() returns 404, the BE has
				// invalidated the token server-side (customer record deleted). The token
				// stored in Redux above is now dead and all subsequent authenticated calls
				// will fail with 401. Correct fix: inspect the error, and if status === 404,
				// dispatch logout() and redirect to the entry page. Deferred because this
				// is an extremely rare race condition and the redirect destination needs a
				// routing decision outside STO-3211 scope.
				logWarning('[VerifyOtpStep] Customer profile fetch failed', {
					error: String(customerError),
				})
			}

			// Show the success screen; the success useEffect handles the
			// delayed onLoginSuccess() (redirect + close) and cleans up on unmount.
			isVerifyingRef.current = false
			setVerifyState('success')
		} catch (err) {
			const apiError = normalizeError(err)
			const errorKey = getVerifyOtpErrorMessageKey({
				status: apiError.status ?? 0,
				message: apiError.message,
				code: apiError.code,
			})
			isVerifyingRef.current = false
			setState((prev) => ({
				...prev,
				isRequestInProgress: false,
				errorMessage: t(errorKey),
			}))
			setVerifyState('failure')
		}
	}

	const handleResendCode = async () => {
		if (!state.channel || !state.identifier) return
		if (isResendingRef.current) return
		isResendingRef.current = true

		setState((prev) => ({
			...prev,
			otpInput: '',
			errorMessage: null,
			isRequestInProgress: true,
		}))

		try {
			const response = await requestOtp({
				channel: state.channel,
				identifier: state.identifier,
				locationId,
			})

			setState((prev) => ({
				...prev,
				correlationId: response.correlationId,
				isRequestInProgress: false,
			}))
			setResendCountdown(RESEND_COUNTDOWN_SECONDS)
			isResendingRef.current = false
		} catch (err) {
			const apiError = normalizeError(err)
			const errorKey = getErrorMessageKey({
				status: apiError.status ?? 0,
				message: apiError.message,
				code: apiError.code,
			})
			isResendingRef.current = false
			setState((prev) => ({
				...prev,
				isRequestInProgress: false,
				errorMessage: t(errorKey),
			}))
			// Do not restart the countdown — leave the resend link active so the user can retry immediately
		}
	}

	const handleChangeIdentifier = () => {
		setState((prev) => ({
			...prev,
			isVerifyStepShown: false,
			otpInput: '',
			errorMessage: null,
			correlationId: null,
		}))
	}

	const handleTryAgain = () => {
		isVerifyingRef.current = false
		setState((prev) => ({
			...prev,
			otpInput: '',
			errorMessage: null,
		}))
		setVerifyState('input')
	}

	const getDisplayIdentifier = (): string => {
		if (!state.identifier) return ''
		if (state.channel === 'SMS') {
			// Format E.164 phone for display, e.g. +11234567890 -> +1 123 456 7890
			const digits = state.identifier.replace(/[^\d+]/g, '')
			const match = digits.match(/^(\+\d)(\d{3})(\d{3})(\d{4})$/)
			if (match) {
				return `${match[1]} ${match[2]} ${match[3]} ${match[4]}`
			}
			return state.identifier
		}
		return state.identifier
	}

	const displayIdentifier = getDisplayIdentifier()
	const isSms = state.channel === 'SMS'

	// Success State
	if (verifyState === 'success') {
		return (
			<StyledContentBox>
				<StyledResultIcon isSuccess>
					<CheckCircleIcon sx={{ fontSize: '64px', color: colors.success }} />
				</StyledResultIcon>

				<StyledResultTitle>{t('signin:verifyOtpStep:welcomeTitle')}</StyledResultTitle>

				<StyledResultMessage>
					{t('signin:verifyOtpStep:welcomeSubtitle')}
				</StyledResultMessage>
			</StyledContentBox>
		)
	}

	// Failure State (Per Figma Design)
	if (verifyState === 'failure') {
		return (
			<>
				<StyledContentBox>
					<StyledResultIcon isSuccess={false}>
						<ErrorIcon sx={{ fontSize: '64px', color: colors.error }} />
					</StyledResultIcon>

					<StyledResultTitle>{t('signin:verifyOtpStep:failureTitle')}</StyledResultTitle>

					<StyledResultMessage>
						{state.errorMessage ?? t('signin:verifyOtpStep:failureMessage')}
					</StyledResultMessage>
				</StyledContentBox>

				<StyledFooterBox>
					<StyledButtonContainer>
						<StyledButton type="button" onClick={handleTryAgain}>
							{t('signin:verifyOtpStep:tryAgainButton')}
						</StyledButton>
					</StyledButtonContainer>
				</StyledFooterBox>
			</>
		)
	}

	// Input State (default)
	return (
		<>
			<StyledContentBox>
				<StyledVerifyContentWrapper>
					<StyledVerifyText>
						{t(
							isSms
								? 'signin:verifyOtpStep:instructionPrefix'
								: 'signin:verifyOtpStep:instructionPrefixEmail',
						)}
					</StyledVerifyText>
					<StyledVerifyTitle>{displayIdentifier}</StyledVerifyTitle>

					<StyledOtpInputContainer>
						{Array.from({ length: 6 }).map((_, index) => (
							<StyledOtpInput
								key={index}
								ref={index === 0 ? firstOtpInputRef : null}
								id={`otp-${index}`}
								type="text"
								inputMode="numeric"
								autoComplete="one-time-code"
								maxLength={6}
								value={otpDigits[index] || ''}
								onChange={(e) => handleOtpChange(index, e.target.value)}
								onKeyDown={(e) => handleKeyDown(index, e)}
								onPaste={handlePaste}
								disabled={state.isRequestInProgress}
								aria-invalid={!!state.errorMessage}
								aria-label={t('signin:verifyOtpStep:digitAriaLabel', {
									position: index + 1,
								})}
							/>
						))}
					</StyledOtpInputContainer>

					{state.errorMessage && (
						<StyledErrorBox role="alert">
							<StyledErrorText>{state.errorMessage}</StyledErrorText>
						</StyledErrorBox>
					)}
				</StyledVerifyContentWrapper>

				<StyledHelperLinksBox>
					{isMobile ? (
						<StyledHelperText>
							{resendCountdown > 0 ? (
								t('signin:verifyOtpStep:resendCountdown', {
									seconds: resendCountdown,
								})
							) : (
								<a
									href="#resend"
									onClick={(e) => {
										e.preventDefault()
										void handleResendCode()
									}}
								>
									{t('signin:verifyOtpStep:resendButton')}
								</a>
							)}
						</StyledHelperText>
					) : (
						<StyledHelperText>
							{resendCountdown > 0 ? (
								t('signin:verifyOtpStep:resendCountdown', {
									seconds: resendCountdown,
								})
							) : (
								<>
									{t('signin:verifyOtpStep:resendPrompt')}{' '}
									<a
										href="#resend"
										onClick={(e) => {
											e.preventDefault()
											void handleResendCode()
										}}
									>
										{t('signin:verifyOtpStep:resendButton')}
									</a>
								</>
							)}
						</StyledHelperText>
					)}
					<StyledHelperText>
						<a
							href="#change-identifier"
							onClick={(e) => {
								e.preventDefault()
								handleChangeIdentifier()
							}}
						>
							{t(
								isSms
									? 'signin:verifyOtpStep:changePhoneButton'
									: 'signin:verifyOtpStep:changeEmailButton',
							)}
						</a>
					</StyledHelperText>
				</StyledHelperLinksBox>
			</StyledContentBox>

			<StyledFooterBox>
				<StyledButtonContainer>
					<StyledButton
						type="button"
						onClick={() => void handleVerifyOtp(state.otpInput)}
						disabled={state.otpInput.length !== 6 || state.isRequestInProgress}
					>
						{state.isRequestInProgress ? (
							<StyledLoadingContent>
								<CircularProgress size={16} sx={{ color: 'inherit' }} />
								{t('signin:verifyOtpStep:verifyingButton')}
							</StyledLoadingContent>
						) : (
							t('signin:verifyOtpStep:verifyButton')
						)}
					</StyledButton>
				</StyledButtonContainer>
			</StyledFooterBox>
		</>
	)
}
